import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { parseYmd, type CalendarView } from "./calendar";
import type { MeetingListParams, MeetingOutcome, SalesMeeting } from "./sales-types";

/**
 * Where somebody is in Meetings, held in the URL.
 *
 * In the query string rather than in component state so that every view is a
 * link -- "my week of 12 October" can be sent to somebody -- and so that a
 * meeting opened over the calendar or the feed carries the view along with it
 * (`/meetings/42?view=week&date=…`) and closing it puts back exactly that view.
 *
 * - `view` is `recent` (the feed) or one of the calendar's three.
 * - `date` anchors the calendar; absent is today.
 * - `who` is `mine` (absent), `team`, or a user id -- whose diary.
 * - `filter` and `q` belong to the feed.
 */
export type MeetingsMode = "recent" | CalendarView;

export interface MeetingsViewState {
  mode: MeetingsMode;
  date: Date;
  who: "mine" | "team" | number;
  filter: MeetingOutcome | null;
  q: string;
}

const MODES: readonly MeetingsMode[] = ["recent", "day", "week", "month"];
const OUTCOMES: readonly MeetingOutcome[] = ["needs_notes", "needs_follow_up", "done"];

/**
 * The calendar view to open on when the URL does not say.
 *
 * Whatever this person last used, remembered per user on this device, then
 * the week -- except on a phone, where seven columns of a working day is too
 * narrow to read and the day is what Google opens on as well.
 */
function rememberedView(userId: number): CalendarView {
  try {
    const stored = window.localStorage.getItem(viewKey(userId));
    if (stored === "day" || stored === "week" || stored === "month") return stored;
  } catch {
    /* Private mode, blocked storage: fall through to the default. */
  }
  return window.matchMedia?.("(max-width: 639px)").matches ? "day" : "week";
}

function viewKey(userId: number): string {
  return `pulse.meetings.view.${userId}`;
}

/**
 * Which view a query string means, the remembered calendar view when it does
 * not say. Exported for the sidebar, which has to light the view that is
 * actually on screen rather than the one the URL happens to spell out.
 */
export function modeOf(params: URLSearchParams, userId: number): MeetingsMode {
  const raw = params.get("view");
  return MODES.includes(raw as MeetingsMode) ? (raw as MeetingsMode) : rememberedView(userId);
}

export function useMeetingsView(userId: number): {
  state: MeetingsViewState;
  set: (patch: Record<string, string | null>) => void;
} {
  const [params, setParams] = useSearchParams();

  const mode = modeOf(params, userId);

  const whoRaw = params.get("who");
  const who: MeetingsViewState["who"] =
    whoRaw === "team" ? "team" : whoRaw && /^\d+$/.test(whoRaw) ? Number(whoRaw) : "mine";

  const filterRaw = params.get("filter");

  const state: MeetingsViewState = {
    mode,
    date: parseYmd(params.get("date")),
    who,
    filter: OUTCOMES.includes(filterRaw as MeetingOutcome) ? (filterRaw as MeetingOutcome) : null,
    q: params.get("q") ?? "",
  };

  /* Remember the calendar view as it changes, so the next visit opens on it. */
  useEffect(() => {
    if (mode === "recent") return;
    try {
      window.localStorage.setItem(viewKey(userId), mode);
    } catch {
      /* Not worth telling anybody about. */
    }
  }, [mode, userId]);

  const set = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        /* Replace, not push: stepping through weeks should not fill the back
           button with every week visited on the way. */
        { replace: true },
      );
    },
    [setParams],
  );

  return { state, set };
}

/** The list filter for whose diary is showing. */
export function whoFilter(who: MeetingsViewState["who"]): Pick<MeetingListParams, "mine" | "attendeeUserId"> {
  if (who === "mine") return { mine: true };
  if (who === "team") return {};
  return { attendeeUserId: who };
}

/**
 * What kind of meeting this is, for its colour.
 *
 * Derived rather than stored. **Internal** is a meeting with nobody from
 * outside: no guests at all, or only guests at our own domains -- the server
 * counts the rest as `externalGuestCount` -- and no customer contact chosen to
 * see. The contact matters because the booking form keeps the guest list only
 * when Google is connected, so a visit booked without it has no guests and is
 * still plainly a customer visit if somebody picked who it is with.
 *
 * Otherwise a visit is in person and a call is online.
 */
export type MeetingTone = "visit" | "call" | "internal" | "cancelled";

export function toneOf(meeting: SalesMeeting): MeetingTone {
  if (meeting.status === "cancelled") return "cancelled";
  if (meeting.externalGuestCount === 0 && meeting.contactId === null) return "internal";
  return meeting.mode === "online" ? "call" : "visit";
}

/** Block colours per tone, light and dark. Left border carries the hue. */
export const TONE_CLASSES: Record<MeetingTone, string> = {
  visit:
    "border-l-sky-600 bg-sky-100 text-sky-950 hover:bg-sky-200 dark:bg-sky-500/20 dark:text-sky-100 dark:hover:bg-sky-500/30",
  call:
    "border-l-violet-600 bg-violet-100 text-violet-950 hover:bg-violet-200 dark:bg-violet-500/20 dark:text-violet-100 dark:hover:bg-violet-500/30",
  internal:
    "border-l-emerald-600 bg-emerald-100 text-emerald-950 hover:bg-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-100 dark:hover:bg-emerald-500/30",
  cancelled:
    "border-l-zinc-400 bg-zinc-100 text-zinc-500 line-through hover:bg-zinc-200 dark:bg-zinc-500/15 dark:text-zinc-400",
};

export const TONE_LABELS: Record<Exclude<MeetingTone, "cancelled">, string> = {
  visit: "Customer visit",
  call: "Online call",
  internal: "Internal",
};

/** What a meeting is called on screen. */
export function meetingTitle(meeting: { subject: string | null; companyName: string }): string {
  return meeting.subject?.trim() || `Meeting with ${meeting.companyName}`;
}

/**
 * The state of a meeting that has been and gone, as one word.
 *
 * Shared by the calendar's corner icon and the feed's dot, so the two never
 * disagree about whether a visit is dealt with.
 */
export type AfterState = "done" | "notes" | "followed-up" | "nothing" | null;

export function afterState(meeting: SalesMeeting, now = new Date()): AfterState {
  if (meeting.status === "cancelled") return null;
  const end = meeting.endAt ? new Date(meeting.endAt) : new Date(meeting.startAt);
  if (end > now) return null;
  const notes = meeting.noteCount > 0;
  const sent = meeting.followUpCount > 0;
  if (notes && sent) return "done";
  if (notes) return "notes";
  if (sent) return "followed-up";
  return "nothing";
}
